/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from "@/components/ui/sheet";
import { Question, BookOpen, Lightning, X } from "@phosphor-icons/react";
import { getHelp, listHelpTopics, HELP } from "@/lib/helpContent";

/**
 * <HelpButton topic="matters" /> — a small ? icon button next to any heading or
 * section title. Click opens a right-side drawer with that topic's content
 * (title, summary, "How to use" steps, tips, related links).
 *
 * If `topic` is omitted, the drawer opens to the home/index where every topic
 * is browsable.
 */
export default function HelpButton({ topic, label = null, variant = "icon", testid }) {
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(topic || null);
    const data = active ? getHelp(active) : null;

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
                <button
                    type="button"
                    onClick={() => setActive(topic || null)}
                    aria-label={label || `Help${topic ? ` · ${topic}` : ""}`}
                    title="Open help"
                    data-testid={testid || `help-btn-${topic || "index"}`}
                    className={
                        variant === "icon"
                            ? "inline-flex items-center justify-center w-6 h-6 rounded-full border border-border text-muted-foreground hover:bg-muted/40 hover:text-foreground"
                            : "inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground border border-border rounded-sm px-2.5 py-1.5"
                    }
                >
                    <Question size={variant === "icon" ? 12 : 14} weight="bold" />
                    {variant !== "icon" && <span>{label || "Help"}</span>}
                </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-md md:max-w-lg p-0 flex flex-col" data-testid="help-drawer">
                <SheetHeader className="px-5 pt-5 pb-3 border-b border-border">
                    <SheetTitle className="font-serif text-xl flex items-center gap-2">
                        <BookOpen size={18} weight="duotone" className="text-primary" />
                        {active ? data?.title || "Help" : "CounselIQ Help"}
                    </SheetTitle>
                    <SheetDescription className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                        {active
                            ? "In-app reference · keep working while you read"
                            : "Pick a topic to read its quick guide"}
                    </SheetDescription>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto px-5 py-4">
                    {active ? (
                        <TopicView data={data} onJump={(k) => setActive(k)} />
                    ) : (
                        <TopicIndex onPick={(k) => setActive(k)} />
                    )}
                </div>

                {active && (
                    <div className="border-t border-border px-5 py-3 flex items-center justify-between text-xs text-muted-foreground" data-testid="help-drawer-footer">
                        <button
                            onClick={() => setActive(null)}
                            className="underline hover:text-foreground"
                            data-testid="help-drawer-browse"
                        >
                            ← Browse all topics
                        </button>
                        <span>
                            <kbd className="px-1 py-0.5 border border-border rounded text-[10px]">Esc</kbd> to close
                        </span>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}

function TopicView({ data, onJump }) {
    if (!data) {
        return (
            <div className="text-sm text-muted-foreground">
                Help content not found for this topic yet. Use the index to browse all topics.
            </div>
        );
    }
    return (
        <div className="space-y-5" data-testid="help-topic-view">
            <p className="text-sm leading-relaxed">{data.summary}</p>

            {data.how && data.how.length > 0 && (
                <section>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2 inline-flex items-center gap-1.5">
                        <Lightning size={11} weight="fill" className="text-primary" /> How to use
                    </div>
                    <ol className="list-decimal list-inside space-y-1.5 text-sm marker:text-muted-foreground">
                        {data.how.map((step, i) => (
                            <li key={i}>{step}</li>
                        ))}
                    </ol>
                </section>
            )}

            {data.tips && data.tips.length > 0 && (
                <section>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">
                        Pro tips
                    </div>
                    <ul className="space-y-1.5 text-sm border-l-2 border-amber-300 bg-amber-50/40 pl-3 py-2 rounded-sm">
                        {data.tips.map((tip, i) => (
                            <li key={i}>{tip}</li>
                        ))}
                    </ul>
                </section>
            )}

            {data.related && data.related.length > 0 && (
                <section>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">
                        Related
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {data.related.map((k) => {
                            const r = getHelp(k);
                            if (!r) return null;
                            return (
                                <button
                                    key={k}
                                    onClick={() => onJump(k)}
                                    className="text-xs border border-border rounded-sm px-2 py-1 text-muted-foreground hover:text-foreground hover:bg-muted/40"
                                    data-testid={`help-jump-${k}`}
                                >
                                    {r.title} →
                                </button>
                            );
                        })}
                    </div>
                </section>
            )}
        </div>
    );
}

function TopicIndex({ onPick }) {
    const groups = useMemo(() => {
        const order = [
            ["Getting around", ["dashboard", "global_search", "voice", "async_jobs"]],
            ["Casework", ["clients", "matters", "strategy", "calendar", "cause_lists", "tasks"]],
            ["Research & drafting", ["research", "court_search", "drafting", "cite_precedent", "clauses", "precedents", "playbooks"]],
            ["Finance & analytics", ["billing", "analytics"]],
            ["Documents & knowledge", ["documents", "vault"]],
            ["Compliance & team", ["compliance", "compliance_conflict", "chambers"]],
            ["Account & admin", ["settings", "admin", "llm_costs"]],
        ];
        return order.map(([label, keys]) => [label, keys.filter((k) => HELP[k])]);
    }, []);
    const allKeys = listHelpTopics();
    return (
        <div className="space-y-5" data-testid="help-topic-index">
            <p className="text-sm text-muted-foreground">
                In-app reference for every page and feature. The full printable guide lives in your
                firm's USER_GUIDE.md.
            </p>
            {groups.map(([label, keys]) => (
                <section key={label}>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">{label}</div>
                    <ul className="space-y-1">
                        {keys.map((k) => (
                            <li key={k}>
                                <button
                                    onClick={() => onPick(k)}
                                    className="w-full text-left px-2 py-1.5 rounded-sm hover:bg-muted/40 flex items-start gap-2"
                                    data-testid={`help-index-${k}`}
                                >
                                    <span className="text-muted-foreground">›</span>
                                    <span>
                                        <span className="font-medium text-sm">{HELP[k].title}</span>
                                        <span className="block text-xs text-muted-foreground line-clamp-1">
                                            {HELP[k].summary}
                                        </span>
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </section>
            ))}
            <div className="text-xs text-muted-foreground border-t border-border pt-3">
                {allKeys.length} topics indexed.
            </div>
        </div>
    );
}
